/* eslint-disable import/no-extraneous-dependencies, react/no-array-index-key */
import { Divider, Grid } from '@mui/material';
import { ActionButton, AddItemButton, BasicButton } from '@tassat/button';
import {
    SelectField,
    SimpleErrorMessage,
    UserRoleField
} from '@tassat/formik-input';
import { Delete } from '@tassat/icon';
import { ClientWalletRolesMap, SelectOption } from '@tassat/ui-definitions';
import { FieldArray, Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateWalletUserMappingForm } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        users,
        values,
        errors,
        features,
        submitForm,
        isSubmitting,
        submitError,
        isValid,
        account
    } = props;
    const [ userOptions, setUserOptions ] = React.useState<SelectOption[]>([]);
    const unmappedUsers = users.length;

    const getAllowedRoles = (accessControl) => {
        const allowedRoles = accessControl ?
            ClientWalletRolesMap.filter((role) => role.label !== 'F')
            : ClientWalletRolesMap.filter((role) => role.label !== 'M' && role.label !== 'A');

        const mappedAllowedRoles = allowedRoles.map((data) => ({
            value: data.id,
            label: data.label
        }));

        return mappedAllowedRoles;
    };

    React.useEffect(() => {
        setUserOptions(
            users
                .filter((userObj) => {
                    const { user } = userObj;
                    let notMapped = true;
                    values.Mappings.forEach((mapping) => {
                        if (mapping.User && user.UserId === mapping.User.value) {
                            notMapped = false;
                        }
                    });

                    return notMapped;
                })
                .map((userObj) => {
                    const { user } = userObj;

                    return {
                        label: `${user.FirstName} ${user.LastName}`,
                        value: user.UserId,
                        refData: userObj
                    };
                })
        );
    }, [JSON.stringify(values.Mappings)]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <FieldArray
                    name='Mappings'
                    render={(arrayHelpers) => (
                        <>
                            {values.Mappings.map((mapping, index) => (
                                <Grid
                                    container
                                    direction='column'
                                    key={`mapping_${index}`}
                                    wrap='nowrap'
                                >
                                    <Grid
                                        className={classes.fullWidth}
                                        container
                                        item
                                        spacing={3}
                                        wrap='nowrap'
                                        xs={12}
                                    >
                                        <Grid
                                            className={classes.userSelectWrapper}
                                            container
                                            justifyContent='space-between'
                                        >
                                            <Grid
                                                className={classes.userSelect}
                                                item
                                                xs='auto'
                                            >
                                                <SelectField
                                                    autoComplete
                                                    errors={errors}
                                                    key={`user_select_${index}`}
                                                    label='Select User'
                                                    name={`Mappings.${index}.User`}
                                                    nestedValue
                                                    options={userOptions}
                                                    values={values}
                                                />
                                            </Grid>
                                            {values.Mappings[index].User && (
                                                <Grid
                                                    className={classes.roles}
                                                    item
                                                >
                                                    <UserRoleField
                                                        allowedRoles={
                                                            getAllowedRoles(
                                                                values.Mappings[index]?.User?.refData.account.AccessControl
                                                            )
                                                        }
                                                        errors={errors}
                                                        key={`role_select_${index}`}
                                                        label='Role'
                                                        name={`Mappings.${index}.Role`}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                        {values.Mappings.length > 1 && (
                                            <Grid
                                                className={classes.removeMappingContainer}
                                                item
                                                xs={1}
                                            >
                                                <ActionButton
                                                    handleClick={() => arrayHelpers.remove(index)}
                                                    Icon={Delete}
                                                    key={`remove_mapping_${index}`}
                                                    text='Delete'
                                                    type='icon'
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                    {values.Mappings.length > 1 &&
                                        index !== values.Mappings.length - 1 && (
                                        <Divider
                                            className={classes.fieldMappingDivider}
                                            orientation='horizontal'
                                            variant='fullWidth'
                                        />
                                    )}
                                </Grid>
                            ))}
                            {userOptions.length > 0 && values.Mappings.length < unmappedUsers && (
                                <Grid
                                    className={classes.newMappingContainer}
                                    container
                                    item
                                    justifyContent='flex-start'
                                    spacing={2}
                                    xs={12}
                                >
                                    <Grid item>
                                        <AddItemButton
                                            onClick={() => arrayHelpers.push({
                                                User: null,
                                                Role: ''
                                            })}
                                            text='Add New Mapping'
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                />
                <Grid
                    container
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <BasicButton
                        className={classes.submitButton}
                        disabled={isSubmitting || !isValid}
                        onClick={submitForm}
                        text='Submit'
                        type='submit'
                    />
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={submitError}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default function WalletUserMappingForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        users,
        account,
        features,
        submitWalletUserMappingForm
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const initialValues: IFormValues = {
        Mappings: [
            {
                User: null,
                Role: '',
                IsSCEnabled: false
            }
        ]
    };

    const walletUserMappingOriginalValidation = (values: IFormValues) => validateWalletUserMappingForm(values);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitWalletUserMappingForm(values, {
                            accountId: account.AccountId,
                            setSubmitError,
                            onClose
                        });
                    }}
                    validate={walletUserMappingOriginalValidation}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            account={account}
                            classes={classes}
                            features={features}
                            formProps={formProps}
                            submitError={submitError}
                            users={users}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}